import { useQuery } from '@apollo/client';
import ModuleShell from '../../components/ModuleShell';
import { ADMIN_DECISIONS_FUNNEL } from '../../graphql/adminQueries';
import { buildRange } from '../../utils/dateRange';

function FunnelBars({ steps }) {
  if (!steps.length) return null;
  return (
    <ul className="funnel-bars" aria-label="Funnel versus targets">
      {steps.map((step) => {
        const actual = step.actualPercent;
        const target = Number(step.targetPercent);
        const hasActual = actual != null && Number.isFinite(Number(actual));
        const actualNumber = hasActual ? Number(actual) : 0;
        const scale = Math.max(100, actualNumber, Number.isFinite(target) ? target : 0, 1);
        const ahead = hasActual && actualNumber >= target;
        return (
          <li key={step.stepId} className="funnel-bar-row">
            <span className="funnel-bar-label">{step.label}</span>
            <div className="funnel-bar-track" aria-hidden="true">
              {hasActual && actualNumber > 0 ? (
                <div
                  className="bar-chart-fill"
                  style={{ width: `${(actualNumber / scale) * 100}%` }}
                />
              ) : null}
              {Number.isFinite(target) ? (
                <span
                  className="funnel-target-mark"
                  style={{ left: `${(target / scale) * 100}%` }}
                />
              ) : null}
            </div>
            <span className="funnel-bar-actual">
              {hasActual ? `${actualNumber}%` : '—'}
            </span>
            <span className="funnel-bar-target">
              {Number.isFinite(target) ? `target ${target}%` : 'target —'}
            </span>
            {hasActual ? (
              <span className={ahead ? 'funnel-ahead' : 'funnel-behind'}>
                {ahead ? 'ahead' : 'behind'}
              </span>
            ) : (
              <span className="status-badge status-badge-pending">Pending data</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function DecisionsModule() {
  const range = buildRange(28);

  const { data, loading, error } = useQuery(ADMIN_DECISIONS_FUNNEL, {
    variables: { range },
  });

  const steps = data?.adminDecisionsFunnel || [];

  return (
    <ModuleShell
      title="Decisions funnel"
      description="28-day rolling funnel versus program targets."
      loading={loading}
      loadingLabel="Loading funnel data…"
      error={error}
      errorLabel="Could not load decisions funnel. Confirm API adminDecisionsFunnel query is deployed."
    >
      {!loading && !error && (
        <>
          <section className="chart-card">
            <h2>Funnel vs targets</h2>
            <p className="chart-hint">
              Compare 28d rolling actuals to certification thresholds. Two consecutive months at
              target required for 10/10 strategic value certification.
            </p>
            <FunnelBars steps={steps} />
          </section>

          <section className="chart-card">
            <h2>Funnel table</h2>
            <div className="table-scroll">
              <table className="data-table funnel-table">
                <thead>
                  <tr>
                    <th scope="col">Funnel step</th>
                    <th scope="col">Target</th>
                    <th scope="col">Actual</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step) => (
                    <tr key={step.stepId}>
                      <td>
                        <span className="funnel-step-label">{step.label}</span>
                      </td>
                      <td>≥ {step.targetPercent}%</td>
                      <td>{step.actualPercent == null ? '—' : `${step.actualPercent}%`}</td>
                      <td>
                        {step.actualPercent == null ? (
                          <span className="status-badge status-badge-pending">Pending data</span>
                        ) : (
                          <span
                            className={`status-badge ${
                              step.actualPercent >= step.targetPercent
                                ? 'status-badge-pass'
                                : 'status-badge-fail'
                            }`}
                          >
                            {step.actualPercent >= step.targetPercent ? 'On target' : 'Below target'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <p className="module-footnote">
            Start and complete rates use first-party event counts. Premium saves count named
            decision scenarios stored on the books. Counts are events, not unique people.
          </p>
        </>
      )}
    </ModuleShell>
  );
}
